import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import type { ReactNode } from "react";
import { Alert } from "./components/Alert";
import { AppShell } from "./components/AppShell";
import { RequireAuth } from "./components/RequireAuth";
import { FullPage, Spinner } from "./components/Spinner";
import { ApiKeysPage } from "./pages/ApiKeysPage";
import { ComingSoonPage } from "./pages/ComingSoonPage";
import { DashboardPage } from "./pages/DashboardPage";
import { LoginPage } from "./pages/LoginPage";
import { SessionsPage } from "./pages/SessionsPage";
import { CompanyDetailPage } from "./pages/crm/CompanyDetailPage";
import { CompanyFormPage } from "./pages/crm/CompanyFormPage";
import { CompaniesPage } from "./pages/crm/CompaniesPage";
import { ContactDetailPage } from "./pages/crm/ContactDetailPage";
import { ContactFormPage } from "./pages/crm/ContactFormPage";
import { ContactsPage } from "./pages/crm/ContactsPage";
import { CrmDashboardPage } from "./pages/crm/CrmDashboardPage";
import { DealsPage } from "./pages/crm/DealsPage";
import { PipelinesPage } from "./pages/crm/PipelinesPage";
import { SupportCategoriesPage } from "./pages/support/SupportCategoriesPage";
import { SupportDashboardPage } from "./pages/support/SupportDashboardPage";
import { SupportSlaPage } from "./pages/support/SupportSlaPage";
import { SupportTicketDetailPage } from "./pages/support/SupportTicketDetailPage";
import { SupportTicketFormPage } from "./pages/support/SupportTicketFormPage";
import { SupportTicketsPage } from "./pages/support/SupportTicketsPage";
import { AccountsPage } from "./pages/billing/AccountsPage";
import { BillingDashboardPage } from "./pages/billing/BillingDashboardPage";
import { InvoiceDetailPage } from "./pages/billing/InvoiceDetailPage";
import { InvoiceFormPage } from "./pages/billing/InvoiceFormPage";
import { InvoicesPage } from "./pages/billing/InvoicesPage";
import { PaymentsPage } from "./pages/billing/PaymentsPage";
import { ProductsPage } from "./pages/billing/ProductsPage";
import { QuoteDetailPage } from "./pages/billing/QuoteDetailPage";
import { QuoteFormPage } from "./pages/billing/QuoteFormPage";
import { QuotesPage } from "./pages/billing/QuotesPage";
import { TaxesPage } from "./pages/billing/TaxesPage";
import { ConfigProvider, useConfig } from "./state/config-context";
import { SessionProvider } from "./state/session-context";

function ConfigGate({ children }: { children: ReactNode }): ReactNode {
  const { status, error, reload, t } = useConfig();

  if (status === "loading") {
    return (
      <FullPage>
        <Spinner label={t("app.loading")} />
      </FullPage>
    );
  }

  if (status === "error") {
    return (
      <FullPage>
        <div className="state-card">
          <h1>{t("error.title")}</h1>
          <Alert tone="error">{t("error.bootstrap")}</Alert>
          {error !== null ? <p className="muted">{error}</p> : null}
          <button type="button" className="btn btn--primary" onClick={reload}>
            {t("app.retry")}
          </button>
        </div>
      </FullPage>
    );
  }

  return children;
}

function AppRoutes(): ReactNode {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />
        <Route path="crm" element={<CrmDashboardPage />} />
        <Route path="crm/companies" element={<CompaniesPage />} />
        <Route path="crm/companies/new" element={<CompanyFormPage />} />
        <Route path="crm/companies/:id" element={<CompanyDetailPage />} />
        <Route path="crm/companies/:id/edit" element={<CompanyFormPage />} />
        <Route path="crm/contacts" element={<ContactsPage />} />
        <Route path="crm/contacts/new" element={<ContactFormPage />} />
        <Route path="crm/contacts/:id" element={<ContactDetailPage />} />
        <Route path="crm/contacts/:id/edit" element={<ContactFormPage />} />
        <Route path="crm/deals" element={<DealsPage />} />
        <Route path="crm/settings/pipelines" element={<PipelinesPage />} />
        <Route path="support" element={<SupportDashboardPage />} />
        <Route path="support/tickets" element={<SupportTicketsPage />} />
        <Route path="support/tickets/new" element={<SupportTicketFormPage />} />
        <Route path="support/tickets/:id" element={<SupportTicketDetailPage />} />
        <Route path="support/categories" element={<SupportCategoriesPage />} />
        <Route path="support/sla" element={<SupportSlaPage />} />
        <Route path="billing" element={<BillingDashboardPage />} />
        <Route path="billing/quotes" element={<QuotesPage />} />
        <Route path="billing/quotes/new" element={<QuoteFormPage />} />
        <Route path="billing/quotes/:id" element={<QuoteDetailPage />} />
        <Route path="billing/quotes/:id/edit" element={<QuoteFormPage />} />
        <Route path="billing/invoices" element={<InvoicesPage />} />
        <Route path="billing/invoices/new" element={<InvoiceFormPage />} />
        <Route path="billing/invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="billing/invoices/:id/edit" element={<InvoiceFormPage />} />
        <Route path="billing/payments" element={<PaymentsPage />} />
        <Route path="billing/accounts" element={<AccountsPage />} />
        <Route path="billing/catalog/products" element={<ProductsPage />} />
        <Route path="billing/settings" element={<TaxesPage />} />
        <Route path="properties" element={<ComingSoonPage titleKey="nav.properties" />} />
        <Route path="porteria" element={<ComingSoonPage titleKey="nav.porteria" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App(): ReactNode {
  return (
    <BrowserRouter>
      <ConfigProvider>
        <ConfigGate>
          <SessionProvider>
            <AppRoutes />
          </SessionProvider>
        </ConfigGate>
      </ConfigProvider>
    </BrowserRouter>
  );
}
